import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { DomSanitizer, SafeResourceUrl, Title } from '@angular/platform-browser';
import { filter, Subject, takeUntil } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';

import { AuthService } from '../services/auth.service';
import { DOMAIN } from '../constants/app.constants';
import { environment } from '../../environments/environment';

type SitePage = 'home' | 'product' | 'pricing' | 'customers' | 'resources' | 'contact' | 'impressum' | 'datenschutz' | 'agb' | 'sign-order-terms';

type IconCard = {
  icon: string;
  title: string;
  text: string;
};

type AnalyticsCard = IconCard & {
  metric: string;
};

type UseCaseMediaCard = {
  title: string;
  text: string;
  image: string;
  action: 'demo' | 'poster';
};

type NavItem = {
  page: SitePage;
  label: string;
  fallbackLabel: string;
  currentLabel: string;
  dePath: string;
  enPath: string;
};

type ProofStatItem = {
  value: string;
  label: string;
  fallbackLabel: string;
  currentLabel: string;
};

type LogoItem = {
  src: string;
  srcset?: string;
  altKey: string;
  width: number;
  height: number;
};

type TestimonialItem = {
  quote: string;
  name: string;
  role: string;
  company: string;
};

type FaqItem = {
  question: string;
  answer: string;
};

@Component({
  selector: 'app-company-landing-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    NzModalModule
  ],
  templateUrl: './company-landing-page.component.html',
  styleUrl: './company-landing-page.component.css'
})
export class CompanyLandingPageComponent implements OnInit, OnDestroy {
  @ViewChild('bookingDemoModal', { static: true }) bookingDemoModalTpl!: TemplateRef<unknown>;
  @ViewChild('bookingMobileFallbackModal', { static: true }) bookingMobileFallbackModalTpl!: TemplateRef<unknown>;

  readonly rawBookingDemoUrl = 'https://bookings.cloud.microsoft/book/ScanafyDemo@processoria.de/';
  bookingDemoUrl!: SafeResourceUrl;
  readonly productVideoPosterUrl = 'assets/product_video_preview.png';
  private readonly productVideoEmbedUrl = this.getProductVideoEmbedUrl(environment.productVideoVimeoUrl);
  readonly productVideoConfigured = !!this.productVideoEmbedUrl;
  productVideoUrl: SafeResourceUrl | null = null;
  productVideoStarted = false;
  heroEyebrow = 'PropTech fuer digitale Immobilienkommunikation';
  heroTitle = '#1 QR-Notfall-Aushang fuer moderne Hausverwaltungen.';
  heroSubtitle = 'Scanafy ersetzt veraltete Papier-Aushaenge durch aktuelle Objektseiten per QR-Code: Notfallkontakte, Dokumente und wichtige Hinweise jederzeit verfuegbar - mehrsprachig und ohne App.';
  heroBookDemoCta = 'Demo buchen';
  heroViewLiveDemoCta = 'Live-Demo ansehen';
  loginCta = 'Login';

  selectedLanguage: 'de' | 'en' = 'de';
  currentPage: SitePage = 'home';
  mobileNavOpen = false;
  showDesktopFreeStartLink = true;
  isLoading = false;
  scanMessage = false;
  selectedPlan: 'free' | 'pro' | 'business' = 'pro';
  recommendedPlan: 'free' | 'pro' | 'business' = 'pro';
  propertyCount = 20;
  upgradeAdvice: string | null = null;
  beforeAfterValue = 50;

  contactForm: FormGroup;
  private readonly useCasePosterPdfPath = 'assets/qr-poster-exemplar.pdf';
  private readonly destroy$ = new Subject<void>();
  private readonly mobileNavMediaQuery = typeof window !== 'undefined'
    ? window.matchMedia('(max-width: 991.98px)')
    : null;
  private readonly onMobileNavBreakpointChange = () => this.updateFreeStartNavVisibility();
  private readonly seoTitleFallbacks: Record<string, string> = {
    'SCN.SEO.HOME_TITLE': 'Scanafy | Digitaler Aushang für moderne Hausverwaltungen',
    'SCN.SEO.PRODUCT_TITLE': 'Produkt | Scanafy',
    'SCN.SEO.PRICING_TITLE': 'Preise | Scanafy',
    'SCN.SEO.CUSTOMERS_TITLE': 'Kunden | Scanafy',
    'SCN.SEO.RESOURCES_TITLE': 'Ressourcen | Scanafy',
    'SCN.SEO.CONTACT_TITLE': 'Kontakt | Scanafy',
    'SCN.SEO.IMPRINT_TITLE': 'Impressum | Scanafy',
    'SCN.SEO.PRIVACY_TITLE': 'Datenschutzerklärung | Scanafy',
    'SCN.SEO.TERMS_TITLE': 'Allgemeine Geschäftsbedingungen | Scanafy',
    'SCN.SEO.SIGN_ORDER_TERMS_TITLE': 'Schilderbestellbedingungen | Scanafy'
  };

  private readonly minCompanyLength = 2;
  private readonly minNameLength = 2;
  private readonly minMessageLength = 20;
  private readonly blockedEmailTlds = new Set(['example', 'invalid', 'localhost', 'test', 'tst']);
  private formLoadedAt = Date.now();
  private readonly submissionCooldownMs = 1200;

  readonly navItems: NavItem[] = [
    { page: 'home', label: 'SCN.NAV.HOME', fallbackLabel: 'Startseite', currentLabel: 'Startseite', dePath: '/', enPath: '/' },
    { page: 'product', label: 'SCN.NAV.PRODUCT', fallbackLabel: 'Produkt', currentLabel: 'Produkt', dePath: '/produkt', enPath: '/product' },
    { page: 'pricing', label: 'SCN.NAV.PRICING', fallbackLabel: 'Preise', currentLabel: 'Preise', dePath: '/preise', enPath: '/pricing' },
    { page: 'customers', label: 'SCN.NAV.CUSTOMERS', fallbackLabel: 'Kunden', currentLabel: 'Kunden', dePath: '/kunden', enPath: '/customers' },
    { page: 'resources', label: 'SCN.NAV.RESOURCES', fallbackLabel: 'Ressourcen', currentLabel: 'Ressourcen', dePath: '/ressourcen', enPath: '/resources' },
    { page: 'contact', label: 'SCN.NAV.CONTACT', fallbackLabel: 'Kontakt', currentLabel: 'Kontakt', dePath: '/kontakt', enPath: '/contact' }
  ];

  readonly proofStats: ProofStatItem[] = [
    { value: '3', label: 'SCN.STATS.FREE_OBJECTS', fallbackLabel: 'Kostenlose Objekte', currentLabel: 'Kostenlose Objekte' },
    { value: '99 €', label: 'SCN.STATS.PRO_PRICE', fallbackLabel: 'pro Jahr fuer 10 Objekte', currentLabel: 'pro Jahr fuer 10 Objekte' },
    { value: '0', label: 'SCN.STATS.NO_APP', fallbackLabel: 'App-Downloads noetig', currentLabel: 'App-Downloads noetig' }
  ];

  readonly painPoints: IconCard[] = [
    { icon: 'bi-file-earmark-x', title: 'SCN.PAIN.PAPER_TITLE', text: 'SCN.PAIN.PAPER_TEXT' },
    { icon: 'bi-car-front', title: 'SCN.PAIN.TRAVEL_TITLE', text: 'SCN.PAIN.TRAVEL_TEXT' },
    { icon: 'bi-exclamation-triangle', title: 'SCN.PAIN.EMERGENCY_TITLE', text: 'SCN.PAIN.EMERGENCY_TEXT' }
  ];

  readonly steps: IconCard[] = [
    { icon: 'bi-building', title: 'SCN.STEPS.ONE_TITLE', text: 'SCN.STEPS.ONE_TEXT' },
    { icon: 'bi-qr-code-scan', title: 'SCN.STEPS.TWO_TITLE', text: 'SCN.STEPS.TWO_TEXT' },
    { icon: 'bi-phone', title: 'SCN.STEPS.THREE_TITLE', text: 'SCN.STEPS.THREE_TEXT' }
  ];

  readonly featureCards: IconCard[] = [
    { icon: 'bi-qr-code-scan', title: 'COMPANY_LANDING_PAGE.ECO_FRIENDLY_QR_CODES', text: 'COMPANY_LANDING_PAGE.ECO_FRIENDLY_QR_CODES_DESCRIPTION' },
    { icon: 'bi-telephone-inbound', title: 'COMPANY_LANDING_PAGE.REAL_TIME_EMERGENCY_UPDATES', text: 'COMPANY_LANDING_PAGE.REAL_TIME_EMERGENCY_UPDATES_DESCRIPTION' },
    { icon: 'bi-layout-text-sidebar-reverse', title: 'COMPANY_LANDING_PAGE.CUSTOMIZABLE_PROPERTY_TEMPLATES', text: 'COMPANY_LANDING_PAGE.GENERATE_PROPERTY_TEMPLATES' },
    { icon: 'bi-pencil-square', title: 'COMPANY_LANDING_PAGE.EASY_CONTACT_UPDATES', text: 'COMPANY_LANDING_PAGE.QUICKLY_UPDATE_CONTACTS' },
    { icon: 'bi-signpost-2', title: 'COMPANY_LANDING_PAGE.BRANDED_FLYERS_PLAQUES', text: 'COMPANY_LANDING_PAGE.GENERATE_BRANDED_FLYERS' },
    { icon: 'bi-translate', title: 'COMPANY_LANDING_PAGE.MULTI_LANGUAGE_SUPPORT', text: 'COMPANY_LANDING_PAGE.LANGUAGE_SUPPORT_DESCRIPTION' },
    { icon: 'bi-file-earmark-arrow-up', title: 'COMPANY_LANDING_PAGE.DOCUMENT_UPLOADS', text: 'COMPANY_LANDING_PAGE.DOCUMENT_UPLOADS_DESCRIPTION' },
    { icon: 'bi-graph-up-arrow', title: 'COMPANY_LANDING_PAGE.ANALYTICS_OVERVIEW', text: 'COMPANY_LANDING_PAGE.ANALYTICS_OVERVIEW_DESCRIPTION' },
    { icon: 'bi-bell', title: 'COMPANY_LANDING_PAGE.EMERGENCY_PUSH_NOTIFICATIONS', text: 'COMPANY_LANDING_PAGE.EMERGENCY_PUSH_NOTIFICATIONS_DESCRIPTION' }
  ];

  readonly analyticsCards: AnalyticsCard[] = [
    { icon: 'bi-graph-up-arrow', title: 'SCAN_TRENDS', text: 'SCAN_TRENDS_DESCRIPTION', metric: '+28%' },
    { icon: 'bi-bell', title: 'UPDATE_ALERTS', text: 'UPDATE_ALERTS_DESCRIPTION', metric: '4' },
    { icon: 'bi-file-earmark-bar-graph', title: 'CONTENT_INTERACTIONS', text: 'CONTENT_INTERACTIONS_DESCRIPTION', metric: '83%' },
    { icon: 'bi-grid-1x2', title: 'HEATMAPS', text: 'HEATMAPS_DESCRIPTION', metric: '24/7' },
  ];

  readonly useCaseMediaCards: UseCaseMediaCard[] = [
    {
      title: 'USE_CASE_POSTER_TITLE',
      text: 'USE_CASE_POSTER_TEXT',
      image: 'assets/use-case-emergency-1.avif',
      action: 'poster'
    },
    {
      title: 'USE_CASE_PHONE_TITLE',
      text: 'USE_CASE_PHONE_TEXT',
      image: 'assets/use-case-emergency-2.avif',
      action: 'demo'
    }
  ];

  readonly useCaseCards: IconCard[] = [
    { icon: 'bi-droplet-half', title: 'USE_CASE_INCIDENT_TITLE', text: 'USE_CASE_INCIDENT_TEXT' },
    { icon: 'bi-folder2-open', title: 'USE_CASE_DOCS_TITLE', text: 'USE_CASE_DOCS_TEXT' },
    { icon: 'bi-translate', title: 'USE_CASE_LANGUAGE_TITLE', text: 'USE_CASE_LANGUAGE_TEXT' },
    { icon: 'bi-signpost-split', title: 'USE_CASE_SIGNS_TITLE', text: 'USE_CASE_SIGNS_TEXT' }
  ];

  readonly logos: LogoItem[] = [
    {
      src: 'assets/partners/digiwoh/digiwoh-95.webp',
      srcset: 'assets/partners/digiwoh/digiwoh-95.webp 1x, assets/partners/digiwoh/digiwoh-190.webp 2x',
      altKey: 'COMPANY_LANDING_PAGE.DIGIWOH_ALT',
      width: 95,
      height: 95
    },
    {
      src: 'assets/customers/von-RUEDEN-Immobilien-GmbH/von-rueden-logo-148.webp',
      srcset: 'assets/customers/von-RUEDEN-Immobilien-GmbH/von-rueden-logo-148.webp 1x, assets/customers/von-RUEDEN-Immobilien-GmbH/von-rueden-logo-296.webp 2x',
      altKey: 'COMPANY_LANDING_PAGE.VON_RUEDEN_ALT',
      width: 148,
      height: 94
    },
    {
      src: 'assets/customers/Fuehrer_und_Brungs/Fuehrer_und_Brungs_logo.png',
      altKey: 'COMPANY_LANDING_PAGE.FUEHRER_BRUNGS_ALT',
      width: 164,
      height: 80
    },
    {
      src: 'assets/customers/Schlag-Immobilien/schlag_immoblien.svg',
      altKey: 'COMPANY_LANDING_PAGE.SCHLAG_IMMOBILIEN_ALT',
      width: 400,
      height: 200
    },
    {
      src: 'assets/customers/Wobau-Hannover-Ost-e_G/wobau_hannover_logo.png',
      altKey: 'COMPANY_LANDING_PAGE.WOBAU_HANNOVER_OST_ALT',
      width: 682,
      height: 196
    },
    {
      src: 'assets/customers/HMP-Immobilien-GmbH/HMP-Logo.png',
      altKey: 'COMPANY_LANDING_PAGE.HMP_ALT',
      width: 137,
      height: 101
    },
    {
      src: 'assets/customers/Fuhrmann-Hausverwaltung-Immobilien/logo-fuhrmann-hausverwaltung-immobilien-farbe.png',
      altKey: 'COMPANY_LANDING_PAGE.FUHRMANN_ALT',
      width: 2524,
      height: 804
    },
    {
      src: 'assets/customers/blohm/blohm-immobilien-hausverwaltung-255.webp',
      srcset: 'assets/customers/blohm/blohm-immobilien-hausverwaltung-255.webp 1x, assets/customers/blohm/blohm-immobilien-hausverwaltung-510.webp 2x',
      altKey: 'COMPANY_LANDING_PAGE.BLOHM_ALT',
      width: 255,
      height: 94
    },
    {
      src: 'assets/customers/BFV/BFV_Logo_rgb.png',
      altKey: 'COMPANY_LANDING_PAGE.BFV_ALT',
      width: 1507,
      height: 1425
    },
    {
      src: 'assets/customers/Alexandra-Bauer/immobilienverwaltung-alexandra-bauer.png',
      altKey: 'COMPANY_LANDING_PAGE.ALEXANDRA_BAUER_ALT',
      width: 400,
      height: 240
    },
    {
      src: 'assets/customers/Simplex-Viviere/simplex-viviere-haus-und-immobilienverwaltungs-UG.png',
      altKey: 'COMPANY_LANDING_PAGE.SIMPLEX_VIVIERE_ALT',
      width: 535,
      height: 216
    },
    {
      src: 'assets/customers/SL-Immobilien-GmbH/sl-immobilien-GmbH.png',
      altKey: 'COMPANY_LANDING_PAGE.SL_IMMOBILIEN_ALT',
      width: 335,
      height: 100
    },
    {
      src: 'assets/customers/M-3-Immobilienverwaltung-GmbH/M3-Logo-276.webp',
      srcset: 'assets/customers/M-3-Immobilienverwaltung-GmbH/M3-Logo-276.webp 1x, assets/customers/M-3-Immobilienverwaltung-GmbH/M3-Logo-552.webp 2x',
      altKey: 'COMPANY_LANDING_PAGE.M3_ALT',
      width: 276,
      height: 95
    },
    {
      src: 'assets/customers/BROKER-OTTO-UG/BROKER_OTTO_UG_logo.png',
      altKey: 'COMPANY_LANDING_PAGE.BROKER_OTTO_ALT',
      width: 856,
      height: 822
    },
    {
      src: 'assets/customers/Esplanade-Real-Estate-Management-GmbH/Esplanade-Logo_2025.png',
      altKey: 'COMPANY_LANDING_PAGE.ESPLANADE_ALT',
      width: 959,
      height: 649
    },
    {
      src: 'assets/customers/imm2-duschinski-watty/imm2-logo-217.webp',
      srcset: 'assets/customers/imm2-duschinski-watty/imm2-logo-217.webp 1x, assets/customers/imm2-duschinski-watty/imm2-logo-434.webp 2x',
      altKey: 'COMPANY_LANDING_PAGE.IMM2_DW_ALT',
      width: 217,
      height: 94
    },
    {
      src: 'assets/customers/Cornelis-Verwaltungs-und-Beteiligungsgesellschaft-mbH/cornelis_logo.png',
      altKey: 'COMPANY_LANDING_PAGE.CORNELIS_ALT',
      width: 193,
      height: 170
    },
    {
      src: 'assets/customers/HVRD-Immobilienverwaltung/hvrd_immobilienverwaltung.png',
      altKey: 'COMPANY_LANDING_PAGE.HVRD_ALT',
      width: 800,
      height: 201
    },
    {
      src: 'assets/customers/IMOBA-GmbH-&-Co-KG/IMOBA_GmbH_&_Co_KG.png',
      altKey: 'COMPANY_LANDING_PAGE.IMOBA_ALT',
      width: 210,
      height: 63
    },
    {
      src: 'assets/customers/ABG-Immobilienmanagement-GmbH/abg_logo.png',
      altKey: 'COMPANY_LANDING_PAGE.ABG_ALT',
      width: 100,
      height: 78
    }
  ];

  readonly featuredLogos: LogoItem[] = [
    this.logos[0],
    this.logos[1],
    this.logos[2],
    this.logos[7],
    this.logos[12],
    this.logos[15]
  ];

  readonly testimonials: TestimonialItem[] = [
    {
      quote: 'SCN.TESTIMONIALS.BLOHM_QUOTE',
      name: 'Frank Henric Blohm',
      role: 'SCN.TESTIMONIALS.BLOHM_ROLE',
      company: 'BLOHM Immobilien Hausverwaltung'
    },
    {
      quote: 'SCN.TESTIMONIALS.BAUER_QUOTE',
      name: 'Alexandra Bauer',
      role: 'SCN.TESTIMONIALS.BAUER_ROLE',
      company: 'Bauer Immobilien'
    },
    {
      quote: 'SCN.TESTIMONIALS.MARR_QUOTE',
      name: 'Sascha Marr',
      role: 'SCN.TESTIMONIALS.MARR_ROLE',
      company: 'M 3 Immobilienverwaltung GmbH'
    },
    {
      quote: 'SCN.TESTIMONIALS.WESTWOOD_QUOTE',
      name: 'Jennifer Westwood',
      role: 'SCN.TESTIMONIALS.WESTWOOD_ROLE',
      company: 'Esplanade Real Estate Management GmbH'
    }
  ];

  readonly faqItems: FaqItem[] = [
    { question: 'SCN.FAQ.Q1', answer: 'SCN.FAQ.A1' },
    { question: 'SCN.FAQ.Q2', answer: 'SCN.FAQ.A2' },
    { question: 'SCN.FAQ.Q3', answer: 'SCN.FAQ.A3' },
    { question: 'SCN.FAQ.Q4', answer: 'SCN.FAQ.A4' },
    { question: 'SCN.FAQ.Q5', answer: 'SCN.FAQ.A5' }
  ];

  constructor(
      private readonly fb: FormBuilder,
      private readonly authService: AuthService,
      private readonly message: NzMessageService,
      private readonly translate: TranslateService,
      private readonly sanitizer: DomSanitizer,
      private readonly modal: NzModalService,
      private readonly router: Router,
      private readonly route: ActivatedRoute,
      private readonly titleService: Title,
  ) {
    const savedLanguage = localStorage.getItem('selectedLanguage');
    this.selectedLanguage = savedLanguage === 'en' || savedLanguage === 'de' ? savedLanguage : 'de';

    this.contactForm = this.fb.group({
      company: ['', [Validators.required, this.trimmedMinLengthValidator(this.minCompanyLength)]],
      name: ['', [Validators.required, this.trimmedMinLengthValidator(this.minNameLength)]],
      email: ['', [Validators.required, Validators.email, this.blockedEmailDomainValidator()]],
      phone: ['', [Validators.required, this.germanPhoneValidator()]],
      message: ['', [Validators.required, this.trimmedMinLengthValidator(this.minMessageLength), Validators.maxLength(2000), this.messageVarietyValidator()]],
      website: ['']
    });
  }

  ngOnInit(): void {
    this.bookingDemoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.rawBookingDemoUrl);
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    this.translate.use(this.selectedLanguage);
    this.updatePlanBasedOnPropertyCount();
    this.updateAboveFoldCopy();
    this.syncPageFromRoute();
    this.bindRouteScroll();
    this.updateFreeStartNavVisibility();
    this.mobileNavMediaQuery?.addEventListener('change', this.onMobileNavBreakpointChange);

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateAboveFoldCopy();
        this.updateTitleForCurrentRoute();
      });
  }

  ngOnDestroy(): void {
    this.mobileNavMediaQuery?.removeEventListener('change', this.onMobileNavBreakpointChange);
    this.destroy$.next();
    this.destroy$.complete();
  }

  scrollToTop(): void {
    this.scrollEverywhereToTop('smooth');
  }

  private scrollEverywhereToTop(behavior: ScrollBehavior = 'auto'): void {
    window.scrollTo({ top: 0, left: 0, behavior });

    document.documentElement.scrollTo?.({
      top: 0,
      left: 0,
      behavior
    });

    document.body.scrollTo?.({
      top: 0,
      left: 0,
      behavior
    });

    const possibleScrollContainers = [
      document.querySelector('.scanafy-site'),
      document.querySelector('.company-landing-page'),
      document.querySelector('.scn-page'),
      document.querySelector('main'),
      document.querySelector('.app-shell'),
      document.querySelector('.content')
    ];

    possibleScrollContainers.forEach(container => {
      if (container instanceof HTMLElement) {
        container.scrollTo?.({
          top: 0,
          left: 0,
          behavior
        });
      }
    });
  }

  private forceTopAfterNavigation(): void {
    this.scrollEverywhereToTop('auto');

    requestAnimationFrame(() => {
      this.scrollEverywhereToTop('auto');

      requestAnimationFrame(() => {
        this.scrollEverywhereToTop('auto');
      });
    });

    [80, 180, 360].forEach(delay => {
      setTimeout(() => this.scrollEverywhereToTop('auto'), delay);
    });
  }

  get navPath(): (item: NavItem) => string {
    return (item: NavItem) => this.selectedLanguage === 'de' ? item.dePath : item.enPath;
  }

  get freeStartPath(): string {
    return this.selectedLanguage === 'de' ? '/kostenlos-starten' : '/start-free';
  }

  private updateFreeStartNavVisibility(): void {
    this.showDesktopFreeStartLink = !this.mobileNavMediaQuery?.matches;
  }

  get totalPrice(): number {
    return this.calculateTotal();
  }

  get normalizedPropertyCount(): number {
    const p = Math.round(Number(this.propertyCount) || 1);
    return Math.max(1, Math.min(500, p));
  }

  get dynamicROI(): { hoursSaved: number; complaintReductionPercent: number; reprintSavings: number } {
    const count = this.propertyCount || 0;
    const hoursSaved = count * 0.5;
    const directReprintSavings = count * 3;
    const managerLaborCost = count * 20;
    const totalReprintSavings = directReprintSavings + managerLaborCost;

    return {
      hoursSaved,
      complaintReductionPercent: 40,
      reprintSavings: totalReprintSavings
    };
  }

  get selectedPlanLabel(): string {
    if (this.recommendedPlan === 'free') return 'SCN.PRICING.FREE_TITLE';
    if (this.recommendedPlan === 'business') return 'SCN.PRICING.BUSINESS_RECOMMENDED';
    return 'SCN.PRICING.PRO_TITLE';
  }

  changeLanguage(language: 'de' | 'en'): void {
    this.selectedLanguage = language;
    this.translate.use(language);
    localStorage.setItem('selectedLanguage', language);

    const item = this.navItems.find(nav => nav.page === this.currentPage);
    if (item) {
      this.router.navigateByUrl(language === 'de' ? item.dePath : item.enPath);
    } else if (this.currentPage === 'impressum') {
      this.router.navigateByUrl(this.getLegalRoute('imprint'));
    } else if (this.currentPage === 'datenschutz') {
      this.router.navigateByUrl(this.getLegalRoute('privacy'));
    } else if (this.currentPage === 'agb') {
      this.router.navigateByUrl(this.getLegalRoute('terms'));
    } else if (this.currentPage === 'sign-order-terms') {
      this.router.navigateByUrl(this.getLegalRoute('signOrderTerms'));
    } else {
      this.syncPageFromRoute();
    }
  }

  navigateTop(path: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.mobileNavOpen = false;

    if (this.router.url.split('?')[0].split('#')[0] === path) {
      this.forceTopAfterNavigation();
      return;
    }

    this.router.navigateByUrl(path).then(() => {
      this.forceTopAfterNavigation();
    });
  }

  getLegalRoute(type: 'imprint' | 'privacy' | 'terms' | 'signOrderTerms'): string {
    const en = this.selectedLanguage === 'en';
    if (type === 'imprint') return en ? '/legal-notice' : '/impressum';
    if (type === 'privacy') return en ? '/privacy' : '/datenschutz';
    if (type === 'signOrderTerms') return en ? '/sign-order-terms' : '/schilderbestellbedingungen';
    return en ? '/terms' : '/agb';
  }

  toggleMobileNav(): void {
    this.mobileNavOpen = !this.mobileNavOpen;
  }

  openApp(event?: Event): void {
    if (event) {
      this.openExternal(event, 'https://app.scanafy.de/login');
      return;
    }
    this.openUrlInNewTab('https://app.scanafy.de/login');
  }

  openRegistration(event?: Event): void {
    if (event) {
      this.openExternal(event, 'https://app.scanafy.de/register');
      return;
    }
    this.openUrlInNewTab('https://app.scanafy.de/register');
  }

  openBookingDemoModal(): void {
    const isMobile = window.matchMedia('(max-width: 767.98px)').matches;

    this.modal.create({
      nzTitle: this.translate.instant('SCN.BOOKING.MODAL_TITLE'),
      nzContent: isMobile ? this.bookingMobileFallbackModalTpl : this.bookingDemoModalTpl,
      nzFooter: null,
      nzClosable: true,
      nzMaskClosable: true,
      nzCentered: true,
      nzWidth: isMobile ? 'min(420px, calc(100vw - 32px))' : '760px',
      nzBodyStyle: { padding: '0', overflow: 'hidden' },
      nzWrapClassName: 'scanafy-booking-modal'
    });
  }

  closeAllModals(): void {
    this.modal.closeAll();
  }

  simulateQRScan(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.scanMessage = true;
    setTimeout(() => {
      this.scanMessage = false;
      this.openUrlInNewTab(this.getDemoLandingPageUrl());
    }, 650);
  }

  openDemoLandingPage(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.openUrlInNewTab(this.getDemoLandingPageUrl());
  }

  startProductVideo(): void {
    const embedUrl = this.productVideoEmbedUrl;
    if (!embedUrl) {
      return;
    }

    this.productVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    this.productVideoStarted = true;
  }

  updatePropertyCount(value: number | string): void {
    const next = Math.round(Number(value) || 1);
    this.propertyCount = Math.max(1, Math.min(500, next));
    this.updatePlanBasedOnPropertyCount();
  }

  openExternal(event: Event, url: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.openUrlInNewTab(url);
  }

  openUseCasePosterPdf(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.openUrlInNewTab(this.useCasePosterPdfPath);
  }

  openUseCaseDemoLandingPage(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.openUrlInNewTab(this.getDemoLandingPageUrl());
  }

  private calculateProPackageCost(p: number): number {
    if (p <= 0) return 0;
    if (p <= 10) return 99;
    return 99 + (p - 10) * 9.99;
  }

  calculateTotal(): number {
    const p = this.propertyCount || 0;

    if (p <= 3) {
      return 0;
    }

    switch (this.selectedPlan) {
      case 'pro':
        return this.calculateProPackageCost(p);
      case 'business':
      default:
        return 0;
    }
  }

  updatePlanBasedOnPropertyCount(): void {
    let p = Math.round(this.propertyCount || 0);
    if (p < 1) p = 1;
    if (p > 500) p = 500;
    this.propertyCount = p;

    this.upgradeAdvice = null;

    if (p <= 3) {
      this.selectedPlan = 'free';
      this.recommendedPlan = 'free';
      return;
    }

    this.selectedPlan = 'pro';
    this.recommendedPlan = 'pro';

    if (p >= 150) {
      this.recommendedPlan = 'business';
      this.upgradeAdvice = this.translate.instant('ADVICE_CONTACT_BUSINESS');
    }
  }

  formatPrice(value: number): string {
    const locale = this.selectedLanguage === 'de' ? 'de-DE' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  formatPriceShort(value: number): string {
    const locale = this.selectedLanguage === 'de' ? 'de-DE' : 'en-US';
    const hasDecimals = value % 1 !== 0;

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: hasDecimals ? 2 : 0
    }).format(value);
  }

  resetCalculator(): void {
    this.propertyCount = 20;
    this.updatePlanBasedOnPropertyCount();
  }

  isInvalid(controlName: string): boolean {
    const control = this.contactForm.get(controlName);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getErrorMessage(controlName: string): string {
    const control = this.contactForm.get(controlName);

    if (!control || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return this.translate.instant('SCN.FORM.ERRORS.REQUIRED');
    }

    if (control.errors['email'] || control.errors['blockedDomain']) {
      return this.translate.instant('SCN.FORM.ERRORS.EMAIL');
    }

    if (control.errors['minlength']) {
      return this.translate.instant('SCN.FORM.ERRORS.MIN_LENGTH', {
        requiredLength: control.errors['minlength'].requiredLength
      });
    }

    if (control.errors['trimmedMinLength']) {
      return this.translate.instant('SCN.FORM.ERRORS.MIN_LENGTH', {
        requiredLength: control.errors['trimmedMinLength'].requiredLength
      });
    }

    if (control.errors['maxlength']) {
      return this.translate.instant('SCN.FORM.ERRORS.MAX_LENGTH', {
        requiredLength: control.errors['maxlength'].requiredLength
      });
    }

    if (control.errors['pattern'] || control.errors['invalidPhone']) {
      return this.translate.instant('SCN.FORM.ERRORS.INVALID_FORMAT');
    }

    return this.translate.instant('SCN.FORM.ERRORS.INVALID');
  }

  onSubmit(): void {
    this.markContactFormTouched();

    if (this.contactForm.get('website')?.value) {
      this.message.error(this.translate.instant('SCN.FORM.BOT_DETECTED'));
      this.resetContactForm();
      return;
    }

    if (Date.now() - this.formLoadedAt < this.submissionCooldownMs) {
      this.message.error(this.translate.instant('SCN.FORM.SUBMISSION_TOO_FAST'));
      return;
    }

    const sanitized = this.getSanitizedFormValues();
    this.contactForm.patchValue(sanitized, { emitEvent: false });
    this.contactForm.updateValueAndValidity();

    if (this.contactForm.invalid) {
      this.markContactFormTouched();
      return;
    }

    this.isLoading = true;
    this.authService
        .contactUs(sanitized.name, sanitized.email, sanitized.phone, sanitized.message, sanitized.company)
        .subscribe({
          next: () => {
            this.isLoading = false;
            this.message.success(this.translate.instant('SCN.FORM.SUCCESS'));
            this.resetContactForm();
          },
          error: () => {
            this.isLoading = false;
            this.message.error(this.translate.instant('SCN.FORM.ERROR'));
          }
        });
  }

  trackByIndex(index: number): number {
    return index;
  }

  private syncPageFromRoute(): void {
    let child = this.route.snapshot;
    while (child.firstChild) child = child.firstChild;

    const routeLanguage = child.data?.['lang'] as 'de' | 'en' | undefined;

    if (routeLanguage && this.selectedLanguage !== routeLanguage) {
      this.selectedLanguage = routeLanguage;
      localStorage.setItem('selectedLanguage', routeLanguage);
      this.translate.use(routeLanguage);
    }

    const page = child.data?.['page'] as SitePage | undefined;
    this.currentPage = page ?? 'home';
    this.updateTitleForCurrentRoute(child.data?.['titleKey'] as string | undefined);
  }

  private updateTitleForCurrentRoute(titleKey?: string): void {
    const pageTitleMap: Record<SitePage, string> = {
      home: 'SCN.SEO.HOME_TITLE',
      product: 'SCN.SEO.PRODUCT_TITLE',
      pricing: 'SCN.SEO.PRICING_TITLE',
      customers: 'SCN.SEO.CUSTOMERS_TITLE',
      resources: 'SCN.SEO.RESOURCES_TITLE',
      contact: 'SCN.SEO.CONTACT_TITLE',
      impressum: 'SCN.SEO.IMPRINT_TITLE',
      datenschutz: 'SCN.SEO.PRIVACY_TITLE',
      agb: 'SCN.SEO.TERMS_TITLE',
      'sign-order-terms': 'SCN.SEO.SIGN_ORDER_TERMS_TITLE'
    };

    this.setTranslatedTitle(titleKey || pageTitleMap[this.currentPage] || 'SCN.SEO.HOME_TITLE');
  }

  private setTranslatedTitle(key: string): void {
    this.translate.get(key)
      .pipe(takeUntil(this.destroy$))
      .subscribe((translated: string) => {
        const fallback = this.seoTitleFallbacks[key] || 'Scanafy';
        this.titleService.setTitle(translated && translated !== key ? translated : fallback);
      });
  }

  private updateAboveFoldCopy(): void {
    this.translate.get([
      'SCN.HERO.EYEBROW',
      'SCN.HERO.TITLE',
      'SCN.HERO.SUBTITLE',
      'SCN.CTA.BOOK_DEMO',
      'SCN.CTA.VIEW_LIVE_DEMO',
      'SCN.CTA.LOGIN',
      ...this.navItems.map((item) => item.label),
      ...this.proofStats.map((item) => item.label)
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((copy: Record<string, string>) => {
        this.heroEyebrow = copy['SCN.HERO.EYEBROW'] && copy['SCN.HERO.EYEBROW'] !== 'SCN.HERO.EYEBROW'
          ? copy['SCN.HERO.EYEBROW']
          : this.heroEyebrow;
        this.heroTitle = copy['SCN.HERO.TITLE'] && copy['SCN.HERO.TITLE'] !== 'SCN.HERO.TITLE'
          ? copy['SCN.HERO.TITLE']
          : this.heroTitle;
        this.heroSubtitle = copy['SCN.HERO.SUBTITLE'] && copy['SCN.HERO.SUBTITLE'] !== 'SCN.HERO.SUBTITLE'
          ? copy['SCN.HERO.SUBTITLE']
          : this.heroSubtitle;
        this.heroBookDemoCta = copy['SCN.CTA.BOOK_DEMO'] && copy['SCN.CTA.BOOK_DEMO'] !== 'SCN.CTA.BOOK_DEMO'
          ? copy['SCN.CTA.BOOK_DEMO']
          : this.heroBookDemoCta;
        this.heroViewLiveDemoCta = copy['SCN.CTA.VIEW_LIVE_DEMO'] && copy['SCN.CTA.VIEW_LIVE_DEMO'] !== 'SCN.CTA.VIEW_LIVE_DEMO'
          ? copy['SCN.CTA.VIEW_LIVE_DEMO']
          : this.heroViewLiveDemoCta;
        this.loginCta = copy['SCN.CTA.LOGIN'] && copy['SCN.CTA.LOGIN'] !== 'SCN.CTA.LOGIN'
          ? copy['SCN.CTA.LOGIN']
          : this.loginCta;

        this.navItems.forEach((item) => {
          const translated = copy[item.label];
          item.currentLabel = translated && translated !== item.label
            ? translated
            : item.fallbackLabel;
        });

        this.proofStats.forEach((item) => {
          const translated = copy[item.label];
          item.currentLabel = translated && translated !== item.label
            ? translated
            : item.fallbackLabel;
        });
      });
  }

  private bindRouteScroll(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.syncPageFromRoute();
        this.mobileNavOpen = false;

        const tree = this.router.parseUrl(this.router.url);
        const hasFragment = !!tree.fragment;

        if (hasFragment) {
          setTimeout(() => {
            document.getElementById(tree.fragment!)?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }, 80);
          return;
        }

        this.forceTopAfterNavigation();
      });
  }

  private getDemoLandingPageUrl(): string {
    const DEMO = {
      company: '680298d599fc0bbf54b94248',
      property: '68032e4d3d2950976b129c59'
    };

    return `${DOMAIN}/landing-page/${DEMO.company}/${DEMO.property}`;
  }

  private getProductVideoEmbedUrl(rawUrl: string): string | null {
    if (!rawUrl) {
      return null;
    }

    try {
      const source = new URL(rawUrl);
      const host = source.hostname.toLowerCase();
      const allowedHost = host === 'vimeo.com' || host === 'www.vimeo.com' || host === 'player.vimeo.com';
      if (!allowedHost) {
        return null;
      }

      const segments = source.pathname.split('/').filter(Boolean);
      const videoId = host === 'player.vimeo.com'
        ? segments[0] === 'video' ? segments[1] : undefined
        : segments[0];
      if (!videoId || !/^\d+$/.test(videoId)) {
        return null;
      }

      const playerUrl = new URL(`https://player.vimeo.com/video/${videoId}`);
      const unlistedHash = source.searchParams.get('h');
      if (unlistedHash && /^[A-Za-z0-9]+$/.test(unlistedHash)) {
        playerUrl.searchParams.set('h', unlistedHash);
      }
      playerUrl.searchParams.set('autoplay', '1');
      playerUrl.searchParams.set('controls', '1');
      playerUrl.searchParams.set('playsinline', '1');
      playerUrl.searchParams.set('title', '0');
      playerUrl.searchParams.set('byline', '0');
      playerUrl.searchParams.set('portrait', '0');
      playerUrl.searchParams.set('dnt', '1');
      return playerUrl.toString();
    } catch {
      return null;
    }
  }

  private openUrlInNewTab(url: string): void {
    const newTab = window.open(url, '_blank', 'noopener,noreferrer');

    if (newTab) {
      newTab.opener = null;
    }
  }

  private trimmedMinLengthValidator(minLength: number): ValidatorFn {
    return (control: AbstractControl) => {
      const rawValue = typeof control.value === 'string' ? control.value : '';
      const trimmedValue = rawValue.trim();
      if (!trimmedValue) return { trimmedMinLength: { requiredLength: minLength, actualLength: 0 } };
      return trimmedValue.length < minLength
          ? { trimmedMinLength: { requiredLength: minLength, actualLength: trimmedValue.length } }
          : null;
    };
  }

  private blockedEmailDomainValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const email = this.sanitizeEmail(control.value);
      if (!email.includes('@')) return null;
      const domain = email.split('@')[1] ?? '';
      const tld = domain.split('.').pop() ?? '';
      return this.blockedEmailTlds.has(tld) ? { blockedDomain: true } : null;
    };
  }

  private messageVarietyValidator(): ValidatorFn {
    return (control: AbstractControl) => {
      const value = this.sanitizeText(control.value).replace(/\s+/g, '').toLowerCase();
      if (!value) return null;
      return new Set(value.split('')).size >= 3 ? null : { variety: true };
    };
  }

  private germanPhoneValidator(): ValidatorFn {
    const allowedCharacters = /^[0-9+()/\.\s-]+$/;
    return (control: AbstractControl) => {
      const raw = typeof control.value === 'string' ? control.value.trim() : '';
      if (!raw) return null;
      if (!allowedCharacters.test(raw)) return { invalidPhone: true };
      const digitCount = raw.replace(/\D/g, '').length;
      return digitCount >= 3 && digitCount <= 15 ? null : { invalidPhone: true };
    };
  }

  private getSanitizedFormValues(): { company: string; name: string; email: string; phone: string; message: string } {
    const { company, name, email, phone, message } = this.contactForm.value;
    return {
      company: this.sanitizeText(company),
      name: this.sanitizeText(name),
      email: this.sanitizeEmail(email),
      phone: this.sanitizePhone(phone),
      message: this.sanitizeText(message)
    };
  }

  private sanitizeText(value: unknown): string {
    return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
  }

  private sanitizeEmail(value: unknown): string {
    return this.sanitizeText(value).toLowerCase();
  }

  private sanitizePhone(value: unknown): string {
    return typeof value === 'string' ? value.replace(/[^0-9+()/\.\s-]/g, '').replace(/\s+/g, ' ').trim() : '';
  }

  private resetContactForm(): void {
    this.contactForm.reset({ company: '', name: '', email: '', phone: '', message: '', website: '' });
    this.contactForm.markAsPristine();
    this.contactForm.markAsUntouched();
    this.formLoadedAt = Date.now();
  }

  private markContactFormTouched(): void {
    Object.values(this.contactForm.controls).forEach(control => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });
  }
}
